import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex justify-center items-center h-full"
}
const _hoisted_2 = {
  key: 1,
  class: "flex flex-col justify-center items-center h-full"
}
const _hoisted_3 = { class: "text-red-500 mb-4" }
const _hoisted_4 = {
  key: 2,
  class: "h-full flex flex-col justify-between"
}
const _hoisted_5 = {
  key: 0,
  class: "space-y-6"
}
const _hoisted_6 = { class: "flex items-center justify-between border-b border-secondary pb-2" }
const _hoisted_7 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_8 = {
  key: 0,
  class: "text-xs text-white bg-secondary opacity-80 px-2 py-1 rounded font-bold"
}
const _hoisted_9 = { class: "space-y-4" }
const _hoisted_10 = { class: "space-y-3" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "col-span-1 text-gray-500" }
const _hoisted_13 = { class: "col-span-5 font-medium" }
const _hoisted_14 = { class: "col-span-3" }
const _hoisted_15 = { class: "col-span-3" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 0,
  class: "py-4 text-center text-gray-500"
}
const _hoisted_18 = { class: "space-y-3" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "col-span-1 text-gray-500" }
const _hoisted_21 = { class: "col-span-5 font-medium" }
const _hoisted_22 = { class: "col-span-3" }
const _hoisted_23 = { class: "col-span-3" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = {
  key: 0,
  class: "py-4 text-center text-gray-500"
}
const _hoisted_26 = { key: 1 }
const _hoisted_27 = { class: "flex mb-5 items-center gap-3 border-b border-secondary" }
const _hoisted_28 = { class: "py-2 text-3xl font-bold text-gray-700" }
const _hoisted_29 = { key: 0 }
const _hoisted_30 = ["for"]
const _hoisted_31 = { class: "flex" }
const _hoisted_32 = ["id", "onUpdate:modelValue"]
const _hoisted_33 = ["onUpdate:modelValue"]
const _hoisted_34 = {
  key: 1,
  class: "py-8 text-center text-gray-500"
}

import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useTasksStore } from "@/stores/v2";
import { storeToRefs } from "pinia";
import { normalizeRouteParam } from "@/utils/v2/routes";
import { TasksService } from "@/services/v2/tasks";
import { useTask } from "@/composables/tasks/useTask";
import { useNotifications } from "@/composables/useNotifications";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import LayoutMain from "@/components/v2/Layouts/LayoutMain.vue";
import { Tank, TaskEnvironmental } from "@/types/v2";


export default /*@__PURE__*/_defineComponent({
  __name: 'EnvironmentalCheckTaskPage',
  setup(__props) {

const route = useRoute();
const tasksStore = useTasksStore();
const { activeTask } = storeToRefs(tasksStore);
const { startObserver } = useTask();
const { showTaskCompletionSuccess, showError } = useNotifications();

const isLoading = ref(true);
const error = ref('');
const editingMode = ref(false);
const activeTank = ref<Tank | null>(null);
const formData = ref<Record<string, string>>({});
const selectedUnits = ref<Record<string, string>>({});
const commentsModal = ref(false);
const instructionsModal = ref(false);

const isEnvironmentalTask = (task: any): task is TaskEnvironmental => task?.objective?.id === 'environmental';

const allTanks = computed(() => {
    if (!activeTask.value || !isEnvironmentalTask(activeTask.value)) return [];
    return activeTask.value.details.tanks.map((tank: Tank) => ({
        ...tank,
        status: tank.status || "pending"
    }));
});

const requiredTanks = computed(() => allTanks.value.filter(tank => tank.required));
const optionalTanks = computed(() => allTanks.value.filter(tank => !tank.required));

const attrTemplateData = [
    { name: "temperature", value: "25", label: "Temperature", unit: ["C°"] },
    { name: "saturation", value: "7", label: "O2 saturation", unit: ["%", "mg/L"] },
    { name: "ph", value: "7", label: "pH", unit: ["(no unit)"] },
    { name: "nitrite", value: "3", label: "Nitrite NO2-N", unit: ["(mg/L)"] },
    { name: "ammonia", value: "2", label: "Ammonia NH3", unit: ["(mg/L)"] },
    { name: "salinity", value: "1", label: "Salinity", unit: ["(‰)"] },
];

const getUnits = (key: string): string[] | null => {
    const attribute = attrTemplateData.find((attr) => attr.name === key);
    return attribute ? attribute.unit : null;
};

const getLabel = (key: string): string => {
    const attribute = attrTemplateData.find((attr) => attr.name === key);
    return attribute ? attribute.label : key;
};

const initTask = async () => {
    isLoading.value = true;
    error.value = '';

    const taskKey = normalizeRouteParam(route.params.key);
    if (!taskKey) {
        error.value = 'Invalid task key';
        isLoading.value = false;
        return;
    }

    try {
        tasksStore.resetActiveTaskState();
        const task = await TasksService.startTask(taskKey);
        if (!task) {
            error.value = 'Task not found';
            isLoading.value = false;
            return;
        }
        await tasksStore.setActiveTask(task);
        startObserver();
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load task';
    } finally {
        isLoading.value = false;
    }
};

const initFormData = () => {
    formData.value = {};
    selectedUnits.value = {};

    if (!activeTank.value) return;

    if (activeTank.value.requiredAttributes?.length) {
        activeTank.value.requiredAttributes.forEach(attr => {
            // Special case: If the attribute name is 'o2', we need to use 'saturation' in our form
            const formFieldName = attr.name === 'o2' ? 'saturation' : attr.name;

            // Try to find existing measures
            let value = '';
            let unit = '';

            if (activeTank.value?.measures) {
                // First check for the actual field name (o2)
                if (attr.name === 'o2' && activeTank.value.measures.o2) {
                    value = activeTank.value.measures.o2.value || '';
                    unit = activeTank.value.measures.o2.unit || '';
                }
                // Then check for saturation as fallback
                else if (attr.name === 'o2' && activeTank.value.measures.saturation) {
                    value = activeTank.value.measures.saturation.value || '';
                    unit = activeTank.value.measures.saturation.unit || '';
                }
                // Other attributes use normal field names
                else if (activeTank.value.measures[attr.name]) {
                    value = activeTank.value.measures[attr.name].value || '';
                    unit = activeTank.value.measures[attr.name].unit || '';
                }
            }

            formData.value[formFieldName] = value;

            // Set default unit if none found
            if (!unit) {
                const unitOptions = getUnits(formFieldName);
                unit = unitOptions ? unitOptions[0] : '';
            }
            selectedUnits.value[formFieldName] = unit;
        });
    } else {
        attrTemplateData.forEach(attr => {
            formData.value[attr.name] = "";
            selectedUnits.value[attr.name] = attr.unit[0];
        });
    }
};

const setActiveTank = (tank: Tank) => {
    activeTank.value = tank;
    editingMode.value = true;
    initFormData();
};

const handleSave = () => {
    if (!activeTank.value || !activeTask.value || !isEnvironmentalTask(activeTask.value)) return;

    const combinedData: Record<string, { value: string; unit: string }> = {};

    // First get all form values
    for (const key in formData.value) {
        if (Object.prototype.hasOwnProperty.call(formData.value, key)) {
            combinedData[key] = {
                value: formData.value[key],
                unit: selectedUnits.value[key] || "",
            };
        }
    }

    // Map saturation to o2 for API compatibility
    if (combinedData.saturation) {
        combinedData.o2 = {
            value: combinedData.saturation.value,
            unit: combinedData.saturation.unit
        };
    }

    const tank = activeTask.value.details.tanks.find((t: Tank) => t.key === activeTank.value?.key);
    if (tank) {
        // Create a proper measures object with all required fields
        const measures = {
            temperature: combinedData.temperature || { value: '', unit: 'C°' },
            saturation: combinedData.saturation || { value: '', unit: '%' },
            ph: combinedData.ph || { value: '', unit: '(no unit)' },
            nitrite: combinedData.nitrite || { value: '', unit: '(mg/L)' },
            ammonia: combinedData.ammonia || { value: '', unit: '(mg/L)' },
            salinity: combinedData.salinity || { value: '', unit: '(‰)' },
            ...combinedData
        };
        tank.measures = measures;
        tank.status = "Completed";
    }

    editingMode.value = false;
};

const handleBack = () => editingMode.value = false;
const handleTankClick = (tank: Tank) => setActiveTank(tank);
const toggleCommentsModal = () => commentsModal.value = !commentsModal.value;
const toggleInstructionsModal = () => instructionsModal.value = !instructionsModal.value;

const handleCompleteTask = async () => {
    if (!activeTask.value) return;

    if (requiredTanks.value.some(tank => tank.status !== "Completed")) {
        alert("Please complete all required tanks before finishing the task.");
        return;
    }

    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title, '/dashboard/sites');
    } catch (error) {
        console.error('Error completing environmental task:', error);
        showError("Task Completion Failed", "Failed to complete the environmental task. Please try again.");
    }
};

watch(() => activeTask.value, (newTask) => {
    if (newTask && isEnvironmentalTask(newTask) && newTask.details?.tanks) {
        newTask.details.tanks.forEach((tank: Tank) => {
            if (!tank.status) tank.status = "pending";
        });
    }
}, { immediate: true });

onMounted(initTask);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LayoutMain, null, {
    mainArea: _withCtx(() => [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, _cache[0] || (_cache[0] = [
            _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary" }, null, -1)
          ])))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, _toDisplayString(error.value), 1),
              _createElementVNode("button", {
                onClick: initTask,
                class: "px-4 py-2 bg-primary text-white rounded"
              }, "Retry")
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (!editingMode.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("h1", _hoisted_7, _toDisplayString(_unref(activeTask)?.title) + " - Overview", 1),
                      (_unref(activeTask)?.studyTitle)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(activeTask).studyTitle), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      _cache[3] || (_cache[3] = _createElementVNode("h2", { class: "font-bold text-xl text-primary mb-2" }, "Required tanks", -1)),
                      _createElementVNode("div", _hoisted_10, [
                        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "grid grid-cols-12 px-4 py-2 bg-gray-50 rounded-t font-medium text-sm text-gray-600" }, [
                          _createElementVNode("div", { class: "col-span-1" }, "#"),
                          _createElementVNode("div", { class: "col-span-5" }, "Tank"),
                          _createElementVNode("div", { class: "col-span-3" }, "Status"),
                          _createElementVNode("div", { class: "col-span-3" }, "Actions")
                        ], -1)),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(requiredTanks.value, (tank, index) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: tank.key,
                            class: "grid grid-cols-12 px-4 py-3 bg-white border border-gray-100 rounded hover:bg-gray-50 cursor-pointer",
                            onClick: ($event: any) => (handleTankClick(tank))
                          }, [
                            _createElementVNode("div", _hoisted_12, _toDisplayString(index + 1), 1),
                            _createElementVNode("div", _hoisted_13, _toDisplayString(tank.label), 1),
                            _createElementVNode("div", _hoisted_14, [
                              _createElementVNode("span", {
                                class: _normalizeClass(["px-2 py-1 text-xs font-semibold rounded-full", {
                                     'bg-green-100 text-green-800': tank.status === 'Completed',
                                     'bg-yellow-100 text-yellow-800': tank.status === 'pending'
                                   }])
                              }, _toDisplayString(tank.status === 'Completed' ? 'Completed' : 'Pending'), 3)
                            ]),
                            _createElementVNode("div", _hoisted_15, [
                              _createElementVNode("button", {
                                class: "px-3 py-1 text-sm text-white bg-primary rounded hover:bg-primary-dark",
                                onClick: _withModifiers(($event: any) => (handleTankClick(tank)), ["stop"])
                              }, " View Details ", 8, _hoisted_16)
                            ])
                          ], 8, _hoisted_11))
                        }), 128)),
                        (requiredTanks.value.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_17, " No required tanks available. "))
                          : _createCommentVNode("", true)
                      ]),
                      _cache[4] || (_cache[4] = _createElementVNode("h2", { class: "font-bold text-xl text-primary mt-4 mb-2" }, "Optional tanks", -1)),
                      _createElementVNode("div", _hoisted_18, [
                        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "grid grid-cols-12 px-4 py-2 bg-gray-50 rounded-t font-medium text-sm text-gray-600" }, [
                          _createElementVNode("div", { class: "col-span-1" }, "#"),
                          _createElementVNode("div", { class: "col-span-5" }, "Tank"),
                          _createElementVNode("div", { class: "col-span-3" }, "Status"),
                          _createElementVNode("div", { class: "col-span-3" }, "Actions")
                        ], -1)),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(optionalTanks.value, (tank, index) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: tank.key,
                            class: "grid grid-cols-12 px-4 py-3 bg-white border border-gray-100 rounded hover:bg-gray-50 cursor-pointer",
                            onClick: ($event: any) => (handleTankClick(tank))
                          }, [
                            _createElementVNode("div", _hoisted_20, _toDisplayString(index + 1), 1),
                            _createElementVNode("div", _hoisted_21, _toDisplayString(tank.label), 1),
                            _createElementVNode("div", _hoisted_22, [
                              _createElementVNode("span", {
                                class: _normalizeClass(["px-2 py-1 text-xs font-semibold rounded-full", {
                                         'bg-green-100 text-green-800': tank.status === 'Completed',
                                         'bg-yellow-100 text-yellow-800': tank.status === 'pending'
                                       }])
                              }, _toDisplayString(tank.status === 'Completed' ? 'Completed' : 'Pending'), 3)
                            ]),
                            _createElementVNode("div", _hoisted_23, [
                              _createElementVNode("button", {
                                class: "px-3 py-1 text-sm text-white bg-primary rounded hover:bg-primary-dark",
                                onClick: _withModifiers(($event: any) => (handleTankClick(tank)), ["stop"])
                              }, " View Details ", 8, _hoisted_24)
                            ])
                          ], 8, _hoisted_19))
                        }), 128)),
                        (optionalTanks.value.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_25, " No optional tanks available. "))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _createVNode(FooterActions, null, {
                      start: _withCtx(() => [
                        _createElementVNode("button", {
                          onClick: toggleInstructionsModal,
                          class: "w-full"
                        }, "Instructions")
                      ]),
                      middle: _withCtx(() => [
                        _createElementVNode("button", {
                          onClick: toggleCommentsModal,
                          class: "w-full"
                        }, "Comment")
                      ]),
                      end: _withCtx(() => [
                        _createElementVNode("button", {
                          onClick: handleCompleteTask,
                          class: "w-full"
                        }, "Complete task")
                      ]),
                      _: 1
                    })
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_26, [
                    _createElementVNode("div", _hoisted_27, [
                      _createElementVNode("h1", _hoisted_28, _toDisplayString(activeTank.value?.label), 1)
                    ]),
                    (Object.keys(formData.value).length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formData.value, (value, key, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: index,
                              class: "mb-5 w-full flex flex-col"
                            }, [
                              _createElementVNode("label", {
                                for: `input-${index}`,
                                class: "text-sm text-primary mb-2"
                              }, _toDisplayString(getLabel(key)), 9, _hoisted_30),
                              _createElementVNode("div", _hoisted_31, [
                                _withDirectives(_createElementVNode("input", {
                                  id: `input-${index}`,
                                  "onUpdate:modelValue": ($event: any) => ((formData.value[key]) = $event),
                                  class: "w-[90%] border p-2 border-gray-300 text-sm text-gray-600 mb-2",
                                  step: "0.01",
                                  type: "number"
                                }, null, 8, _hoisted_32), [
                                  [_vModelText, formData.value[key]]
                                ]),
                                (getUnits(key))
                                  ? _withDirectives((_openBlock(), _createElementBlock("select", {
                                      key: 0,
                                      "onUpdate:modelValue": ($event: any) => ((selectedUnits.value[key]) = $event),
                                      class: "w-[10%] text-sm p-1 mb-2 text-center bg-primary text-white"
                                    }, [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getUnits(key), (unit, unitIndex) => {
                                        return (_openBlock(), _createElementBlock("option", { key: unitIndex }, _toDisplayString(unit), 1))
                                      }), 128))
                                    ], 8, _hoisted_33)), [
                                      [_vModelSelect, selectedUnits.value[key]]
                                    ])
                                  : _createCommentVNode("", true)
                              ])
                            ]))
                          }), 128))
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_34, " No measure requirements found for this tank. ")),
                    _createVNode(FooterActions, null, {
                      start: _withCtx(() => [
                        _createElementVNode("button", {
                          onClick: handleBack,
                          class: "w-full"
                        }, "Back")
                      ]),
                      end: _withCtx(() => [
                        _createElementVNode("button", {
                          onClick: handleSave,
                          class: "w-full"
                        }, "Save")
                      ]),
                      _: 1
                    })
                  ]))
            ]))
    ]),
    _: 1
  }))
}
}

})